import { Component, ElementRef, HostBinding, HostListener, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { Product } from 'idt-ecommerce-api/catalog/products/products.models';
import { ProductsService } from 'idt-ecommerce-api/catalog/products/products.service';
import { CMSEvent } from 'render/models/cms-event.model';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, first, map, take, takeUntil } from 'rxjs/operators';
import { EmbedVideoService } from 'shared/embed-video/embed-video.service';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { Orientation } from 'shared/models/idt-components/idt-component.models';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';
import { toNumber } from 'shared/utils/data-utils';

import { PieceVideoSettings, VideoOptions } from './models/piece-video.models';
import { styleUrls, templateUrl } from './piece-video-render.component.paths';
import { getWysiwygAligmentClasses, TypeOfVideo, TypePositionOfAudioIcon } from './piece-video-utils';


interface Skeleton {
  height: string;
  width: string;
}

@Component({
  selector: 'piece-video-render',
  templateUrl,
  styleUrls
})
export class PieceVideoRenderComponent
  extends BaseRenderComponent<PieceVideoSettings>
  implements OnInit, OnChanges, OnDestroy {

  @HostBinding('class') public aligmentClasses: string | undefined;

  public skeleton$!: Observable<Skeleton>;
  public autoPlay: number = 0;
  public loop: number = 0;
  public controls: number = 0;
  public isMuted: boolean = false;
  public orientation$!: Observable<Orientation>;
  public isVimeoOnline$!: Observable<boolean>;
  public videoSrc$!: Observable<string | undefined>;
  public isLoadingVideo: boolean = false;
  private orientation: Orientation = 'landscape';
  private isVimeoOnline: boolean = true;
  private isKeyboardNavigation: boolean = false;

  constructor(
    private readonly embedService: EmbedVideoService,
    private readonly idtSharedRenderService: IdtSharedRenderService,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    private readonly productService: ProductsService,
    private readonly renderByDeviceService: RenderByDeviceService,
    private readonly element: ElementRef
  ) {
    super();
  }

  @HostListener('document:keydown', ['$event'])
  public onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      this.isKeyboardNavigation = true;
    }
  }

  public ngOnInit() {
    if (!!this.settings && this.typeOfVideo !== 'own') {
      this.autoPlay = this.settings.general.autoPlay ? 1 : 0;
      this.loop = this.settings.general.loop ? 1 : 0;
      this.controls = this.settings.general.showControls ? 1 : 0;
      this.isLoadingVideo = true;
    }
    this.isMuted = !!this.settings.general.autoPlay;

    if (this.typeOfVideo === 'vimeo') {
      this.isVimeoOnline$ = this.embedService.isVimeoOnline().pipe(first());
    } else {
      this.isVimeoOnline$ = of(true);
    }

    this.orientation$ = (this.idtSharedRenderService.screenOrientation$ as Observable<Orientation>).pipe(
      distinctUntilChanged()
    );

    this.orientation$.pipe(
      takeUntil(this.clearSubscriptions$)
    ).subscribe((orientation: Orientation) => {
      this.orientation = orientation;
    });

    this.skeleton$ = this.orientation$.pipe(
      map((orientation) => this.getSkeleton(orientation))
    );

    this.videoSrc$ = combineLatest([this.orientation$, this.isVimeoOnline$]).pipe(
      map(([orientation, isVimeoOnline]) => (
        this.selectOwnOrExternalVideo(orientation, isVimeoOnline)
      ))
    );
  }

  public ngOnChanges() {
    if (this.piece) {
      this.aligmentClasses = getWysiwygAligmentClasses(this.piece);
    }
  }

  @HostBinding('class.marquee')
  public get isMarqueeTextAnimationEnabled(): boolean {
    return !!this.settings.general?.marquee?.marqueeStatus;
  }

  @HostBinding('style.--marquee-duration')
  public get marqueeTextDuration(): string | undefined {
    if (this.isMarqueeTextAnimationEnabled) {
      if (this.renderByDeviceService.isMobile) {
        const marqueeDurationMobileValue = this.settings.general?.marquee?.marqueeDurationMobile;
        return marqueeDurationMobileValue ? `${marqueeDurationMobileValue}s` : '5s';
      }
      const marqueeDurationDesktopValue = this.settings.general?.marquee?.marqueeDurationDesktop;
      return marqueeDurationDesktopValue ? `${marqueeDurationDesktopValue}s` : '20s';
    }
    return undefined;
  }

  @HostBinding('style.--imageObjectFit')
  public get imageObjectFit(): string | undefined {
    return this.settings.styles?.objectFit;
  }

  @HostBinding('style.--imageObjectPosition')
  public get imageObjectPosition(): string | undefined {
    return this.settings.styles?.objectPosition;
  }

  @HostBinding('style.--video-min-width')
  public get videoMinWidth(): string {
    if (this.typeOfVideo === 'vimeo') {
      if (this.orientation === 'landscape' && !!this.settings.general?.ratioVimeoLandscape) {
        const ratio = this.settings.general.ratioVimeoLandscape.split(':');
        return `${(+ratio[0] / +ratio[1]) * 100}vh`;
      }

      if (this.orientation === 'portrait' && !!this.settings.general?.ratioVimeoPortrait) {
        const ratio = this.settings.general.ratioVimeoPortrait.split(':');
        return `${(+ratio[0] / +ratio[1]) * 100}vh`;
      }
    }

    if (this.typeOfVideo === 'youtube') {
      if (this.orientation === 'landscape' && !!this.settings.general?.ratioYoutubeLandscape) {
        const ratio = this.settings.general.ratioYoutubeLandscape.split(':');
        return `${(+ratio[0] / +ratio[1]) * 100}vh`;
      }

      if (this.orientation === 'portrait' && this.settings.general?.ratioYoutubePortrait !== undefined) {
        const ratio = this.settings.general.ratioYoutubePortrait.split(':');
        return `${(+ratio[0] / +ratio[1]) * 100}vh`;
      }
    }

    return '100%';
  }

  @HostBinding('style.--video-height')
  public get videoHeight(): string {
    if (this.typeOfVideo === 'vimeo') {
      if (this.orientation === 'landscape' && !!this.settings.general?.ratioVimeoLandscape) {
        const ratio = this.settings.general.ratioVimeoLandscape.split(':');
        return `${(+ratio[1] / +ratio[0]) * 100}vw`;
      }

      if (this.orientation === 'portrait' && !!this.settings.general?.ratioVimeoPortrait) {
        const ratio = this.settings.general.ratioVimeoPortrait.split(':');
        return `${(+ratio[1] / +ratio[0]) * 100}vw`;
      }
    }

    if (this.typeOfVideo === 'youtube') {
      if (this.orientation === 'landscape' && !!this.settings.general?.ratioYoutubeLandscape) {
        const ratio = this.settings.general.ratioYoutubeLandscape.split(':');
        return `${(+ratio[1] / +ratio[0]) * 100}vw`;
      }

      if (this.orientation === 'portrait' && !!this.settings.general?.ratioYoutubePortrait) {
        const ratio = this.settings.general.ratioYoutubePortrait.split(':');
        return `${(+ratio[1] / +ratio[0]) * 100}vw`;
      }
    }

    return '100%';
  }

  public get typeOfVideo(): TypeOfVideo | undefined {
    if (this.settings.general?.enableFallbackStaticVideo && !this.isVimeoOnline) {
      return 'own' as TypeOfVideo;
    }

    return this.settings.general?.typeOfVideo;
  }

  public getPosterImageSrc(orientation: Orientation): LazyDataInterface | undefined {
    const imageData = orientation === 'portrait' ? this.settings.general.portraitPosterImage : this.settings.general.landscapePosterImage;
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const akamaiOptions: AmandaOptions = {
      isVideoPoster: true,
      isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled,
      width: this.assetsSharedService.getWidth(this.element)
    };
    let result!: LazyDataInterface | undefined;

    if (imageData?.image && imageData?.image !== '') {
      result = {
        src: this.assetsSharedService.getAssetUrl(imageData.image, assetsUrl, akamaiOptions),
        height: toNumber(imageData.height),
        width: toNumber(imageData.width)
      };
    }

    return result;
  }

  public toggleAudioMute(): void {
    this.isMuted = !this.isMuted;
  }

  public get audioIconPosition(): TypePositionOfAudioIcon {
    if (!this.settings.general.showMediaAudio && !this.settings.general.showPositionControls) {
      return 'bottom-left';
    }
    return this.settings.general.audioIconPosition ?? 'top-left';
  }

  public get hasToPlaceIconRelative(): boolean {
    return (
      this.settings.general.audioIconPosition === 'top-text'
      || this.settings.general.audioIconPosition === 'bottom-text'
    );
  }

  public get hasSystemAccessibilityPreferences(): boolean {
    return this.matchesMediaQuery('(prefers-reduced-motion: reduce)')
      || this.matchesMediaQuery('(prefers-contrast: more)')
      || this.matchesMediaQuery('(forced-colors: active)');
  }

  public get shouldShowAccessibilityControls(): boolean {
    return this.hasSystemAccessibilityPreferences || this.isKeyboardNavigation;
  }

  public onRenderLinkClick(productInfo: { productId: string | undefined; categoryId: string | undefined }): void {
    if (productInfo.productId) {
      this.productService.productDetail(productInfo.productId, productInfo.categoryId).pipe(
        take(1)
      ).subscribe((product) => {
        const cmsEvent: CMSEvent<Product> = {
          name: 'productLink',
          type: 'click',
          data: product
        };
        this.cmsRenderEventsService.emitEvent(cmsEvent);
      });
    }
  }

  public ngOnDestroy() {
    this.clearSubscriptions$.next();
    this.clearSubscriptions$.complete();
  }

  private selectOwnOrExternalVideo(orientation: Orientation, isVimeoOnline: boolean): string | undefined {
    this.isVimeoOnline = isVimeoOnline;
    if (this.typeOfVideo === 'own'
      || (this.typeOfVideo === 'vimeo' && !this.isVimeoOnline)
    ) {
      return this.getOwnVideoSrc(orientation);
    }

    return this.getExternalVideoSrc(orientation);
  }

  private getOwnVideoSrc(orientation: Orientation): string | undefined {
    let video: string | undefined;
    if (orientation === 'portrait') {
      video = this.settings.general.videoPortrait ? this.settings.general.videoPortrait : this.settings.general.video;
    } else {
      video = this.settings.general.videoLandscape ? this.settings.general.videoLandscape : this.settings.general.video;
    }

    const assetUrl = this.renderConfigurationService.config.assetsUrl;
    const akamaiOptions: AmandaOptions = { isWidthParamEnabled: false };

    return this.assetsSharedService.getAssetUrl(video, assetUrl, akamaiOptions);
  }

  private getExternalVideoSrc(orientation: Orientation): string | undefined {
    let video;
    let iframeHtml;
    let hasToShowControlsForVimeo = false;

    if (orientation === 'portrait' && this.typeOfVideo === 'youtube') {
      video = this.settings.general.videoYoutubePortrait
        ? this.settings.general.videoYoutubePortrait
        : this.settings.general.videoYoutube;
    } else if (orientation === 'portrait' && this.typeOfVideo === 'vimeo') {
      video = this.settings.general.videoVimeoPortrait
        ? this.settings.general.videoVimeoPortrait
        : this.settings.general.videoVimeo;
    } else if (orientation === 'landscape' && this.typeOfVideo === 'youtube') {
      video = this.settings.general.videoYoutubeLandscape
        ? this.settings.general.videoYoutubeLandscape
        : this.settings.general.videoYoutube;
    } else if (orientation === 'landscape' && this.typeOfVideo === 'vimeo') {
      video = this.settings.general.videoVimeoLandscape
        ? this.settings.general.videoVimeoLandscape
        : this.settings.general.videoVimeo;
    }

    const regex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w-._~:/?#[\]@!$&'()*+,;=]+$/;

    if (this.typeOfVideo === 'vimeo') {
      hasToShowControlsForVimeo = !!this.controls;
    }
    if (regex.exec(video as string)) {
      const options: VideoOptions = {
        query: {
          autoplay: this.autoPlay,
          loop: this.loop,
          controls: this.typeOfVideo === 'vimeo' ? hasToShowControlsForVimeo : this.controls,
          mute: this.autoPlay,
          muted: this.autoPlay
        },
        attr: {
          frameborder: 0,
          width: 50,
          height: 50
        }
      };
      if (this.typeOfVideo === 'youtube' && this.loop) {
        options.query.playlist = this.embedService.getYoutubeId(video as string);
      }
      iframeHtml = this.embedService.embed(video, options);
    } else {
      if (this.typeOfVideo === 'vimeo') {
        iframeHtml = this.embedService.embed_vimeo(video as string, {
          query: {
            autoplay: this.autoPlay,
            loop: this.loop,
            controls: hasToShowControlsForVimeo,
            muted: this.autoPlay
          },
          attr: {
            frameborder: 0,
            width: 50,
            height: 50
          }
        });
      } else if (this.typeOfVideo === 'youtube') {
        iframeHtml = this.embedService.embed_youtube(video as string, {
          query: { autoplay: this.autoPlay, loop: this.loop, controls: this.controls, mute: this.autoPlay }
        });
      }
    }
    this.isLoadingVideo = false;
    return iframeHtml;
  }

  private getSkeleton(orientation: Orientation) {
    const widthImage = this.getPosterImageSrc(orientation)?.width;
    const heightImage = this.getPosterImageSrc(orientation)?.height;
    const heightSkeletonPx = heightImage && widthImage
      ? Math.round(heightImage * (this.element.nativeElement.scrollWidth / widthImage))
      : 0;
    const heightSkeleton = heightSkeletonPx !== 0 ? `${heightSkeletonPx}px` : '100%';

    return {
      height: heightSkeleton,
      width: '100%'
    };
  }

  private matchesMediaQuery(query: string): boolean {
    if (typeof globalThis.window?.matchMedia !== 'function') {
      return false;
    }

    try {
      return globalThis.window.matchMedia(query).matches;
    } catch {
      return false;
    }
  }

}
